import React, {
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRight,
  faCheck,
  faCopy,
  faEye,
  faEyeSlash,
  faKey,
  faLink,
  faRightFromBracket,
  faSave,
  faShieldHalved,
  faUserPen,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  apiClient,
} from "../api/apiClient";

import {
  dashboardUsersApi,
} from "../api/dashboardUsersApi";

import type {
  DashboardUserDetails,
} from "../types/dashboard-users.types";

import "../screenstyles/DashboardUserForm.css";

type RoleItem = {
  roleId: string;
  name: string;
  nameAr?: string | null;
  permissionIds: string[];
  isActive: boolean;
};

type PermissionItem = {
  permissionId: string;
};

type RolesResponse = {
  items?: RoleItem[];
  roles?: RoleItem[];
};

type PermissionsResponse = {
  items?: PermissionItem[];
  permissions?: PermissionItem[];
};

const EditDashboardUser:
React.FC = () => {
  const {
    uid,
  } =
    useParams<{
      uid: string;
    }>();

  const navigate =
    useNavigate();

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    saving,
    setSaving,
  ] =
    useState(false);


    const [
  securityLoading,
  setSecurityLoading,
] =
  useState<
    | "password"
    | "reset-link"
    | "sessions"
    | null
  >(null);

const [
  newPassword,
  setNewPassword,
] =
  useState("");

const [
  confirmPassword,
  setConfirmPassword,
] =
  useState("");

const [
  showPassword,
  setShowPassword,
] =
  useState(false);

const [
  passwordResetLink,
  setPasswordResetLink,
] =
  useState<string | null>(
    null,
  );

  const [
    user,
    setUser,
  ] =
    useState<
      DashboardUserDetails |
      null
    >(null);

  const [
    roles,
    setRoles,
  ] =
    useState<RoleItem[]>(
      [],
    );

  const [
    permissions,
    setPermissions,
  ] =
    useState<
      PermissionItem[]
    >([]);

  const [
    form,
    setForm,
  ] =
    useState({
      fullName: "",
      displayName: "",
      email: "",
      phoneNumber: "",
      roleId: "",
      permissionIds:
        [] as string[],
      isActive: true,
      isSuperAdmin: false,
    });

  useEffect(() => {
    if (!uid) {
      navigate(
        "/dashboard-users",
      );

      return;
    }

    const loadData =
      async () => {
        setLoading(true);

        try {
          const [
            userData,
            rolesData,
            permissionsData,
          ] =
            await Promise.all([
              dashboardUsersApi
                .getById(uid),

              apiClient.get<
                RolesResponse
              >(
                "/roles",
              ),

              apiClient.get<
                PermissionsResponse
              >(
                "/roles/permissions",
              ),
            ]);

          const currentUser =
            userData.user;

          setUser(
            currentUser,
          );

          setRoles(
            (
              rolesData.items ??
              rolesData.roles ??
              []
            ).filter(
              role =>
                role.isActive,
            ),
          );

          setPermissions(
            permissionsData.items ??
              permissionsData.permissions ??
              [],
          );

          setForm({
            fullName:
              currentUser.fullName,

            displayName:
              currentUser.displayName ??
              "",

            email:
              currentUser.email,

            phoneNumber:
              currentUser.phoneNumber ??
              "",

            roleId:
              currentUser.roleId ??
              "",

            permissionIds:
              currentUser
                .permissions
                .directPermissionIds,

            isActive:
              currentUser.isActive,

            isSuperAdmin:
              currentUser.isSuperAdmin,
          });
        } catch (
          error: any
        ) {
          console.error(
            "Failed to load dashboard user:",
            error,
          );

          toast.error(
            error?.message ||
              "فشل تحميل بيانات المستخدم",
          );

          navigate(
            "/dashboard-users",
          );
        } finally {
          setLoading(false);
        }
      };

    loadData();
  }, [
    uid,
    navigate,
  ]);

  const selectedRole =
    useMemo(
      () =>
        roles.find(
          role =>
            role.roleId ===
            form.roleId,
        ) ?? null,
      [
        roles,
        form.roleId,
      ],
    );

  const togglePermission =
    (
      permissionId:
        string,
    ) => {
      setForm(prev => {
        const exists =
          prev.permissionIds.includes(
            permissionId,
          );

        return {
          ...prev,

          permissionIds:
            exists
              ? prev.permissionIds.filter(
                  id =>
                    id !==
                    permissionId,
                )
              : [
                  ...prev.permissionIds,
                  permissionId,
                ],
        };
      });
    };

  const handleSubmit =
    async (
      event:
        FormEvent,
    ) => {
      event.preventDefault();

      if (
        !uid ||
        !user
      ) {
        return;
      }

      setSaving(true);

      try {
        const requests:
          Promise<unknown>[] =
          [];

        const profileChanged =
          form.fullName.trim() !==
            user.fullName ||
          form.displayName.trim() !==
            (user.displayName ??
              "") ||
          form.email.trim() !==
            user.email ||
          form.phoneNumber.trim() !==
            (user.phoneNumber ??
              "");

        if (
          profileChanged
        ) {
          requests.push(
            dashboardUsersApi
              .update(
                uid,
                {
                  fullName:
                    form.fullName.trim(),

                  displayName:
                    form.displayName.trim() ||
                    null,

                  email:
                    form.email.trim(),

                  phoneNumber:
                    form.phoneNumber.trim() ||
                    null,
                },
              ),
          );
        }

        if (
          form.roleId !==
          (user.roleId ?? "")
        ) {
          requests.push(
            dashboardUsersApi
              .updateRole(
                uid,
                form.roleId ||
                  null,
              ),
          );
        }

        const oldPermissions =
          [
            ...user
              .permissions
              .directPermissionIds,
          ].sort();

        const newPermissions =
          [
            ...form
              .permissionIds,
          ].sort();

        if (
          JSON.stringify(
            oldPermissions,
          ) !==
          JSON.stringify(
            newPermissions,
          )
        ) {
          requests.push(
            dashboardUsersApi
              .updatePermissions(
                uid,
                form.permissionIds,
              ),
          );
        }

        if (
          form.isActive !==
          user.isActive
        ) {
          requests.push(
            dashboardUsersApi
              .updateStatus(
                uid,
                form.isActive,
              ),
          );
        }

        if (
          form.isSuperAdmin !==
          user.isSuperAdmin
        ) {
          requests.push(
            dashboardUsersApi
              .updateSuperAdmin(
                uid,
                form.isSuperAdmin,
              ),
          );
        }

        if (
          requests.length ===
          0
        ) {
          toast(
            "لا توجد تغييرات للحفظ",
          );

          return;
        }

        await Promise.all(
          requests,
        );

        toast.success(
          "تم تحديث المستخدم بنجاح",
        );

        navigate(
          "/dashboard-users",
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to update dashboard user:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تحديث المستخدم",
        );
      } finally {
        setSaving(false);
      }
    };

    const handleSetPassword =
  async () => {
    if (!uid) {
      return;
    }

    if (
      newPassword.length <
      8
    ) {
      toast.error(
        "كلمة المرور يجب أن تكون 8 أحرف على الأقل",
      );

      return;
    }

    if (
      newPassword !==
      confirmPassword
    ) {
      toast.error(
        "كلمتا المرور غير متطابقتين",
      );

      return;
    }

    const confirmed =
      window.confirm(
        "سيتم تغيير كلمة مرور المستخدم وإلغاء جلساته الحالية. هل تريد المتابعة؟",
      );

    if (!confirmed) {
      return;
    }

    try {
      setSecurityLoading(
        "password",
      );

      const result =
        await dashboardUsersApi
          .setPassword(
            uid,
            newPassword,
          );

      if (
        result.updated
      ) {
        toast.success(
          result.sessionsRevoked
            ? "تم تغيير كلمة المرور وإلغاء جميع الجلسات بنجاح"
            : "تم تغيير كلمة المرور بنجاح",
        );
      }

      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
    } catch (
      error: any
    ) {
      console.error(
        "Failed to change dashboard user password:",
        error,
      );

      toast.error(
        error?.message ||
          "فشل تغيير كلمة المرور",
      );
    } finally {
      setSecurityLoading(
        null,
      );
    }
  };
  const handleCreateResetLink =
  async () => {
    if (!uid) {
      return;
    }

    try {
      setSecurityLoading(
        "reset-link",
      );

      const result =
        await dashboardUsersApi
          .createPasswordResetLink(
            uid,
          );

      setPasswordResetLink(
        result.resetLink,
      );

      toast.success(
        "تم إنشاء رابط إعادة تعيين كلمة المرور",
      );
    } catch (
      error: any
    ) {
      console.error(
        "Failed to create password reset link:",
        error,
      );

      toast.error(
        error?.message ||
          "فشل إنشاء رابط إعادة تعيين كلمة المرور",
      );
    } finally {
      setSecurityLoading(
        null,
      );
    }
  };

  const handleCopyResetLink =
  async () => {
    if (
      !passwordResetLink
    ) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        passwordResetLink,
      );

      toast.success(
        "تم نسخ الرابط",
      );
    } catch (
      error
    ) {
      console.error(
        "Failed to copy reset link:",
        error,
      );

      toast.error(
        "تعذر نسخ الرابط",
      );
    }
  };

  const handleRevokeSessions =
  async () => {
    if (!uid) {
      return;
    }

    const confirmed =
      window.confirm(
        "سيتم تسجيل خروج المستخدم من جميع الأجهزة والجلسات الحالية. هل أنت متأكد؟",
      );

    if (!confirmed) {
      return;
    }

    try {
      setSecurityLoading(
        "sessions",
      );

      const result =
        await dashboardUsersApi
          .revokeSessions(
            uid,
          );

      if (
        result.revoked
      ) {
        toast.success(
          "تم إلغاء جميع جلسات المستخدم بنجاح",
        );
      }
    } catch (
      error: any
    ) {
      console.error(
        "Failed to revoke dashboard user sessions:",
        error,
      );

      toast.error(
        error?.message ||
          "فشل إلغاء جلسات المستخدم",
      );
    } finally {
      setSecurityLoading(
        null,
      );
    }
  };

  if (loading) {
    return (
      <div className="dashboard-user-form-state">
        جاري تحميل بيانات المستخدم...
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="dashboard-user-form-page">
      <div className="dashboard-user-form-header">
        <div>
          <span className="dashboard-user-form-eyebrow">
            إدارة مستخدمي الداش بورد
          </span>

          <h1>
            تعديل المستخدم
          </h1>

          <p>
            تعديل بيانات الحساب والدور
            والصلاحيات وحالة الوصول.
          </p>
        </div>

        <button
          type="button"
          className="dashboard-user-form-back"
          onClick={() =>
            navigate(
              "/dashboard-users",
            )
          }
        >
          <FontAwesomeIcon
            icon={
              faArrowRight
            }
          />

          رجوع
        </button>
      </div>

      <form
        className="dashboard-user-form-card"
        onSubmit={
          handleSubmit
        }
      >
        <div className="dashboard-user-form-section">
          <div className="dashboard-user-form-section-title">
            <FontAwesomeIcon
              icon={
                faUserPen
              }
            />

            <div>
              <h2>
                البيانات الأساسية
              </h2>

              <p>
                معلومات حساب المستخدم.
              </p>
            </div>
          </div>

          <div className="dashboard-user-form-grid">
            <div className="dashboard-user-field">
              <label>
                الاسم الكامل
              </label>

              <input
                type="text"
                value={
                  form.fullName
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      fullName:
                        event
                          .target
                          .value,
                    }),
                  )
                }
              />
            </div>

            <div className="dashboard-user-field">
              <label>
                الاسم الظاهر
              </label>

              <input
                type="text"
                value={
                  form.displayName
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      displayName:
                        event
                          .target
                          .value,
                    }),
                  )
                }
              />
            </div>

            <div className="dashboard-user-field">
              <label>
                البريد الإلكتروني
              </label>

              <input
                type="email"
                value={
                  form.email
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      email:
                        event
                          .target
                          .value,
                    }),
                  )
                }
              />
            </div>

            <div className="dashboard-user-field">
              <label>
                رقم الهاتف
              </label>

              <input
                type="text"
                value={
                  form.phoneNumber
                }
                onChange={event =>
                  setForm(
                    prev => ({
                      ...prev,
                      phoneNumber:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                dir="ltr"
              />
            </div>
          </div>
        </div>

        <div className="dashboard-user-form-section">
          <div className="dashboard-user-form-section-title">
            <FontAwesomeIcon
              icon={
                faShieldHalved
              }
            />

            <div>
              <h2>
                الدور والصلاحيات
              </h2>

              <p>
                صلاحيات الدور + الصلاحيات المباشرة.
              </p>
            </div>
          </div>

          <div className="dashboard-user-field">
            <label>
              الدور
            </label>

            <select
              value={
                form.roleId
              }
              onChange={event =>
                setForm(
                  prev => ({
                    ...prev,
                    roleId:
                      event
                        .target
                        .value,
                  }),
                )
              }
            >
              <option value="">
                بدون دور
              </option>

              {roles.map(
                role => (
                  <option
                    key={
                      role.roleId
                    }
                    value={
                      role.roleId
                    }
                  >
                    {role.nameAr ||
                      role.name}
                  </option>
                ),
              )}
            </select>
          </div>

          {selectedRole && (
            <div className="dashboard-user-role-summary">
              صلاحيات الدور:{" "}
              <strong>
                {
                  selectedRole
                    .permissionIds
                    .length
                }
              </strong>
            </div>
          )}

          <div className="dashboard-user-permission-grid">
            {permissions.map(
              permission => {
                const checked =
                  form.permissionIds.includes(
                    permission.permissionId
                  );

                return (
                  <button
                    type="button"
                    key={
                    permission.permissionId
                    }
                    className={
                      checked
                        ? "dashboard-user-permission-card selected"
                        : "dashboard-user-permission-card"
                    }
                    onClick={() =>
                      togglePermission(
                       permission.permissionId
                      )
                    }
                  >
                    <span className="dashboard-user-permission-check">
                      {checked && (
                        <FontAwesomeIcon
                          icon={
                            faCheck
                          }
                        />
                      )}
                    </span>

                    <div>
                      <strong>
  {permission.permissionId}
</strong>

<small>
  {permission.permissionId}
</small>
                    </div>
                  </button>
                );
              },
            )}
          </div>
        </div>

        <div className="dashboard-user-form-section">
          <h2>
            حالة الحساب
          </h2>

          <label className="dashboard-user-switch-row">
            <div>
              <strong>
                الحساب نشط
              </strong>

              <span>
                تعطيله يمنع المستخدم من
                تسجيل الدخول.
              </span>
            </div>

            <input
              type="checkbox"
              checked={
                form.isActive
              }
              onChange={event =>
                setForm(
                  prev => ({
                    ...prev,
                    isActive:
                      event
                        .target
                        .checked,
                  }),
                )
              }
            />
          </label>

          <label className="dashboard-user-switch-row dashboard-user-super-admin-row">
            <div>
              <strong>
                Super Admin
              </strong>

              <span>
                صلاحية إدارية شديدة الحساسية.
              </span>
            </div>

            <input
              type="checkbox"
              checked={
                form.isSuperAdmin
              }
              onChange={event =>
                setForm(
                  prev => ({
                    ...prev,
                    isSuperAdmin:
                      event
                        .target
                        .checked,
                  }),
                )
              }
            />
          </label>
        </div>

        <div className="dashboard-user-form-section">
  <div className="dashboard-user-form-section-title">
    <FontAwesomeIcon
      icon={faKey}
    />

    <div>
      <h2>
        الأمان والجلسات
      </h2>

      <p>
        إدارة كلمة المرور،
        رابط الاستعادة،
        والجلسات النشطة للمستخدم.
      </p>
    </div>
  </div>

  <div className="dashboard-user-security-grid">

    <div className="dashboard-user-security-card">
      <div className="dashboard-user-security-card-header">
        <div className="dashboard-user-security-icon">
          <FontAwesomeIcon
            icon={faKey}
          />
        </div>

        <div>
          <h3>
            تغيير كلمة المرور
          </h3>

          <p>
            تعيين كلمة مرور جديدة
            للمستخدم مباشرة.
          </p>
        </div>
      </div>

      <div className="dashboard-user-security-fields">
        <div className="dashboard-user-field">
          <label>
            كلمة المرور الجديدة
          </label>

          <div className="dashboard-user-password-input">
            <input
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              value={
                newPassword
              }
              onChange={event =>
                setNewPassword(
                  event.target.value,
                )
              }
              placeholder="8 أحرف على الأقل"
              dir="ltr"
            />

            <button
              type="button"
              onClick={() =>
                setShowPassword(
                  previous =>
                    !previous,
                )
              }
            >
              <FontAwesomeIcon
                icon={
                  showPassword
                    ? faEyeSlash
                    : faEye
                }
              />
            </button>
          </div>
        </div>

        <div className="dashboard-user-field">
          <label>
            تأكيد كلمة المرور
          </label>

          <input
            type={
              showPassword
                ? "text"
                : "password"
            }
            value={
              confirmPassword
            }
            onChange={event =>
              setConfirmPassword(
                event.target.value,
              )
            }
            placeholder="أعد كتابة كلمة المرور"
            dir="ltr"
          />
        </div>
      </div>

      <button
        type="button"
        className="dashboard-user-security-primary-btn"
        onClick={
          handleSetPassword
        }
        disabled={
          securityLoading !==
          null
        }
      >
        <FontAwesomeIcon
          icon={faKey}
        />

        {securityLoading ===
        "password"
          ? "جاري تغيير كلمة المرور..."
          : "تغيير كلمة المرور"}
      </button>
    </div>

    <div className="dashboard-user-security-card">
      <div className="dashboard-user-security-card-header">
        <div className="dashboard-user-security-icon">
          <FontAwesomeIcon
            icon={faLink}
          />
        </div>

        <div>
          <h3>
            رابط إعادة تعيين كلمة المرور
          </h3>

          <p>
            إنشاء رابط Firebase
            لإعادة تعيين كلمة المرور.
          </p>
        </div>
      </div>

      <button
        type="button"
        className="dashboard-user-security-secondary-btn"
        onClick={
          handleCreateResetLink
        }
        disabled={
          securityLoading !==
          null
        }
      >
        <FontAwesomeIcon
          icon={faLink}
        />

        {securityLoading ===
        "reset-link"
          ? "جاري إنشاء الرابط..."
          : "إنشاء رابط الاستعادة"}
      </button>

      {passwordResetLink && (
        <div className="dashboard-user-reset-link-box">
          <label>
            رابط إعادة التعيين
          </label>

          <div className="dashboard-user-reset-link-row">
            <input
              type="text"
              value={
                passwordResetLink
              }
              readOnly
              dir="ltr"
            />

            <button
              type="button"
              onClick={
                handleCopyResetLink
              }
              title="نسخ الرابط"
            >
              <FontAwesomeIcon
                icon={faCopy}
              />
            </button>
          </div>

        </div>
      )}
    </div>

    <div className="dashboard-user-security-card dashboard-user-security-danger-card">
      <div className="dashboard-user-security-card-header">
        <div className="dashboard-user-security-icon dashboard-user-security-danger-icon">
          <FontAwesomeIcon
            icon={
              faRightFromBracket
            }
          />
        </div>

        <div>
          <h3>
            إلغاء الجلسات
          </h3>

          <p>
            تسجيل خروج المستخدم
            من جميع الأجهزة الحالية.
          </p>
        </div>
      </div>


      <button
        type="button"
        className="dashboard-user-security-danger-btn"
        onClick={
          handleRevokeSessions
        }
        disabled={
          securityLoading !==
          null
        }
      >
        <FontAwesomeIcon
          icon={
            faRightFromBracket
          }
        />

        {securityLoading ===
        "sessions"
          ? "جاري إلغاء الجلسات..."
          : "تسجيل خروج من جميع الأجهزة"}
      </button>
    </div>

  </div>
</div>

        <div className="dashboard-user-form-actions">
          <button
            type="button"
            className="dashboard-user-secondary-button"
            onClick={() =>
              navigate(
                "/dashboard-users",
              )
            }
          >
            إلغاء
          </button>

          <button
            type="submit"
            className="dashboard-user-primary-button"
            disabled={
              saving
            }
          >
            <FontAwesomeIcon
              icon={
                faSave
              }
            />

            {saving
              ? "جاري الحفظ..."
              : "حفظ التغييرات"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDashboardUser;